// Developer: Ankit Gupta
/**
 * Number, money and date formatting shared across the product.
 *
 * It lives in utils rather than inside Insight because Rhino's chart renderer
 * draws Insight's tiles and needs the same rules; a shared module reaching
 * into a feature folder is a dependency pointing the wrong way.
 *
 * One locale and one set of rules for the whole surface, so a value reads the
 * same on an axis, in a tooltip, on a KPI card and in a table. Money is
 * formatted as money - the currency belongs to the number, not to a suffix
 * pinned next to it - and a compact form is only used where space demands it,
 * with the full separated value always available on hover.
 */

/**
 * The locale every value is written in, by interface language.
 *
 * Fixed rather than taken from the browser: two people looking at the same
 * dashboard must read the same number. Arabic is `ar-SA` on the Gregorian
 * calendar with Latin digits (`-u-ca-gregory-nu-latn`), not the Arabic-Indic
 * digits ICU defaults to nor the Hijri calendar some engines default to for
 * Saudi Arabia. A finance team checks these figures and dates against Fusion
 * reports and bank statements printed in Latin digits and Gregorian dates, and
 * switching language must show the same digits in the same order: only the
 * currency symbol, month names, scale words and direction change.
 */
export const LOCALE = 'en-SA';
export const AR_LOCALE = 'ar-SA-u-ca-gregory-nu-latn';
export const CURRENCY = 'SAR';

let uiLanguage: 'en' | 'ar' = 'en';

// The few words these formatters write themselves. English is exactly what the
// screens always said; this module sits under the i18n module, so it keeps its
// own table rather than importing i18next.
const WORDS = {
  en: { neverOpened: 'never opened', justNow: 'just now', notRefreshed: 'not refreshed yet', notMeasured: 'not measured' },
  ar: { neverOpened: 'لم يُفتح بعد', justNow: 'الآن', notRefreshed: 'لم يُحدَّث بعد', notMeasured: 'غير مقيس' },
} as const;

/** The locale the interface writes values in right now. */
export function uiLocale(): string {
  return uiLanguage === 'ar' ? AR_LOCALE : LOCALE;
}

/**
 * The locale for a call that used to pass none (`toLocaleString()`).
 *
 * `undefined` in English keeps exactly what those screens always showed; in
 * Arabic it is the Latin-digit Gregorian locale, never the browser's guess.
 */
export function browserOrUiLocale(): string | undefined {
  return uiLanguage === 'ar' ? AR_LOCALE : undefined;
}

const formats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();

function nf(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const locale = uiLocale();
  const key = `${locale}|${JSON.stringify(options)}`;
  let hit = formats.get(key);
  if (!hit) {
    hit = new Intl.NumberFormat(locale, options);
    formats.set(key, hit);
  }
  return hit;
}

function df(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = uiLocale();
  const key = `${locale}|${JSON.stringify(options)}`;
  let hit = dateFormats.get(key);
  if (!hit) {
    hit = new Intl.DateTimeFormat(locale, options);
    dateFormats.set(key, hit);
  }
  return hit;
}

const FULL = { format: (v: number) => nf({ maximumFractionDigits: 2 }).format(v) };
const FULL_INT = { format: (v: number) => nf({ maximumFractionDigits: 0 }).format(v) };
const COMPACT = {
  format: (v: number) => nf({ notation: 'compact', maximumFractionDigits: 1 }).format(v),
};
const MONEY = {
  format: (v: number) =>
    nf({ style: 'currency', currency: CURRENCY, maximumFractionDigits: 2 }).format(v),
};
const MONEY_COMPACT = {
  format: (v: number) =>
    nf({
      style: 'currency',
      currency: CURRENCY,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(v),
};
const PERCENT = { format: (v: number) => nf({ maximumFractionDigits: 1 }).format(v) };

/** Anything the database hands back, coerced to a number when it is one. */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** The full value with thousands separators: 7,100,000,000. */
export function formatNumber(value: number | null | undefined, fractionDigits = 2): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return (fractionDigits === 0 ? FULL_INT : FULL).format(value);
}

/** The abbreviated value, for axes and other places with no room: 7.1B. */
export function formatCompact(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return COMPACT.format(value);
}

/** SAR 7,100,000,000.00, or SAR 7.1B when compact is asked for. */
export function formatCurrency(
  value: number | null | undefined,
  options: { compact?: boolean } = {},
): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return (options.compact ? MONEY_COMPACT : MONEY).format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return `${PERCENT.format(value)}%`;
}

/**
 * Whether a column holds riyals.
 *
 * Only the columns that say so: the queries mark money with a _SAR suffix, and
 * a bare _AMOUNT is not evidence of a currency. Labelling a figure SAR when it
 * might be dollars is a worse error than leaving it as a plain number.
 */
export function isMoneyColumn(name: string): boolean {
  return /_SAR$|^SAR_/i.test(name);
}

export function isPercentColumn(name: string): boolean {
  return /_PCT$|^PCT_|PERCENT/i.test(name);
}

/**
 * One measure, formatted by what its column name says it is.
 *
 * `compact` is for the places that genuinely run out of room; every caller
 * that uses it should also offer the full value on hover.
 */
export function formatMeasure(
  name: string,
  value: unknown,
  options: { compact?: boolean } = {},
): string {
  const n = toNumber(value);
  if (n === null) return value == null || value === '' ? '-' : String(value);
  if (isPercentColumn(name)) return formatPercent(n);
  if (isMoneyColumn(name)) return formatCurrency(n, options);
  if (options.compact && Math.abs(n) >= 10_000) return formatCompact(n);
  return formatNumber(n);
}

/** The same measure written out in full, for a tooltip or a title attribute. */
export function formatMeasureFull(name: string, value: unknown): string {
  return formatMeasure(name, value);
}

// ── Dates ──────────────────────────────────────────────────────────────────

const AXIS_DATE = {
  format: (d: Date) => df({ day: 'numeric', month: 'short', year: 'numeric' }).format(d),
};
const AXIS_DATETIME = {
  format: (d: Date) =>
    df({ day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(d),
};

/** Matches what Oracle hands back: a date, optionally with a time part. */
const TIMESTAMP_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * A timestamp as a person reads it.
 *
 * The wire carries local wall-clock strings with no zone ("2024-01-15T13:45:00"),
 * so the parts are read directly rather than through Date parsing, which would
 * shift the value by the viewer's offset.
 */
export function formatTimestampLabel(raw: string): string | null {
  const match = TIMESTAMP_RE.exec(raw);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour ?? 0),
    Number(minute ?? 0),
  );
  if (Number.isNaN(date.getTime())) return null;
  // Midnight carries no information, so a plain date stays a plain date.
  const hasTime = hour !== undefined && !(hour === '00' && minute === '00');
  return hasTime ? AXIS_DATETIME.format(date) : AXIS_DATE.format(date);
}

/** "18 minutes ago" / "3 days ago", matching the rail in the product design. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return WORDS[uiLanguage].neverOpened;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return WORDS[uiLanguage].neverOpened;

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return WORDS[uiLanguage].justNow;

  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.345, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  const formatter = new Intl.RelativeTimeFormat(uiLocale(), { numeric: 'auto' });
  let value = seconds;
  for (let i = 0; i < units.length; i += 1) {
    const [size, unit] = units[i];
    if (value < size || unit === 'year') {
      return formatter.format(-Math.round(value), unit);
    }
    value /= size;
  }
  return formatter.format(-Math.round(value), 'year');
}

/**
 * "3 days ago" in the reader's language, or '' for no date.
 *
 * `relativeTime` above is the rail's, fixed to English with its own words for
 * "never" and "just now"; a page that is translated needs the phrase in
 * Arabic too, so this one takes the language and lets Intl write every word.
 */
export function relativeTimeIn(iso: string | null | undefined, language: 'en' | 'ar'): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const formatter = new Intl.RelativeTimeFormat(language === 'ar' ? AR_LOCALE : LOCALE, { numeric: 'auto' });
  let value = Math.round((Date.now() - then) / 1000);
  if (Math.abs(value) < 45) return formatter.format(0, 'second');
  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.345, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  for (const [size, unit] of units) {
    if (Math.abs(value) < size || unit === 'year') return formatter.format(-Math.round(value), unit);
    value /= size;
  }
  return formatter.format(-Math.round(value), 'year');
}

/** Absolute stamp for "Last updated", short on the same day. */
export function stamp(iso: string | null | undefined): string {
  if (!iso) return WORDS[uiLanguage].notRefreshed;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return WORDS[uiLanguage].notRefreshed;
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString(uiLocale(), { hour: '2-digit', minute: '2-digit', hour12: false })
    : date.toLocaleString(uiLocale(), {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
}

/** The full absolute value, for the tooltip behind a relative time. */
export function fullTimestamp(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(uiLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** "1 minute 7 seconds" - the build-time chip on a data source card. */
export function duration(ms: number | null | undefined): string {
  if (ms == null) return WORDS[uiLanguage].notMeasured;
  if (uiLanguage === 'ar') return durationIn(ms, AR_LOCALE);
  if (ms < 1000) return `${ms} ms`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutePart = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  return seconds ? `${minutePart} ${seconds} second${seconds === 1 ? '' : 's'}` : minutePart;
}

// Intl writes the unit with the language's own plural ("3 دقائق", "11 دقيقة").
function durationIn(ms: number, locale: string): string {
  const unit = (value: number, name: string) =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'long' }).format(value);
  if (ms < 1000) return unit(ms, 'millisecond');
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return unit(seconds, 'second');
  return seconds ? `${unit(minutes, 'minute')} ${unit(seconds, 'second')}` : unit(minutes, 'minute');
}

export function fullDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(uiLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Semantic-layer metric formats ──────────────────────────────────────────

/**
 * The `format` a certified metric declares in the semantic layer.
 *
 * These strings come from the metric registry (backend/semantic/metrics/*.yaml
 * and the `sem_metric.format` column), so this union is the frontend half of
 * that contract. An unknown value is not an error: it falls back to a plain
 * number rather than refusing to draw a figure a CFO is waiting for.
 *
 * `percent_*` metrics carry a RATIO on the wire (0.118), because that is what
 * the SQL expression returns and what the thresholds are written against
 * ("< 0.12"). Multiplying by 100 for display happens here, once, so that no
 * caller has to remember it and no tile shows 0.1% where it means 11.8%.
 */
export type MetricFormat =
  | 'currency_0'
  | 'currency_1'
  | 'currency_2'
  | 'percent_0'
  | 'percent_1'
  | 'percent_2'
  | 'number_0'
  | 'number_1'
  | 'number_2'
  | 'days_0'
  | 'days_1';

/**
 * The locale metric values are written in, by interface language.
 *
 * Arabic is `ar-SA` with Latin digits (`-u-nu-latn`), not the Arabic-Indic
 * digits ICU defaults to. A finance team checks these figures against Fusion
 * reports and bank statements printed in Latin digits, and a CFO switching
 * language must see the same digits in the same order: only the currency
 * symbol, the scale words and the direction of the surrounding text change.
 *
 * Every formatter in this module follows the interface language, which the
 * i18n module sets here on start-up and on every change.
 */
const METRIC_LOCALES = { en: LOCALE, ar: AR_LOCALE } as const;

export type MetricLanguage = keyof typeof METRIC_LOCALES;

let metricLanguage: MetricLanguage = 'en';

/** Called by the i18n module on start-up and whenever the language changes. */
export function setMetricLanguage(language: MetricLanguage): void {
  metricLanguage = language;
  uiLanguage = language;
}

export function getMetricLanguage(): MetricLanguage {
  return metricLanguage;
}

/**
 * One terminal mark, never two.
 *
 * A sentence built around a formatted value can end on the value's own full
 * stop - the Arabic riyal symbol "ر.س." ends in one - and then add its own,
 * which prints "ر.س..". Two full stops that are not part of an ellipsis
 * collapse to one, and a full stop straight after a question or exclamation
 * mark (Latin or Arabic) is dropped. A real ellipsis is left alone.
 *
 * ICU closes an Arabic currency with an invisible right-to-left mark
 * ("ر.س." then U+200F), so the two stops are not adjacent in the string even
 * though they are on the screen. Directional marks between them are kept, and
 * do not stop the second stop from going.
 */
const BIDI_MARKS = '\u200E\u200F\u061C\u2066-\u2069';
// Neither stop may be part of a longer run, marks or no marks between them.
const DOUBLE_STOP = new RegExp(
  `(?<!\\.[${BIDI_MARKS}]*)\\.([${BIDI_MARKS}]*)\\.(?![${BIDI_MARKS}]*\\.)`,
  'g',
);
const STOP_AFTER_MARK = new RegExp(`([?!؟][${BIDI_MARKS}]*)\\.(?=\\s|$)`, 'g');

export function tidyPunctuation(text: string): string {
  return text.replace(DOUBLE_STOP, '.$1').replace(STOP_AFTER_MARK, '$1');
}

// The words a metric value carries with it, per language. "days" arrives from
// the registry's unit column; "pts" is how a percentage-point delta is written.
const UNIT_WORDS: Record<MetricLanguage, Record<string, string>> = {
  en: { days: 'days', pts: 'pts' },
  ar: { days: 'يوم', pts: 'نقطة' },
};

const unitWord = (unit: string, language: MetricLanguage): string => UNIT_WORDS[language][unit] ?? unit;

const METRIC_DECIMALS = /_(\d)$/;

const decimalsOf = (format: string, fallback: number): number => {
  const match = METRIC_DECIMALS.exec(format);
  return match ? Number(match[1]) : fallback;
};

const cache = new Map<string, Intl.NumberFormat>();

const numberFormat = (options: Intl.NumberFormatOptions, locale: string): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const made = new Intl.NumberFormat(locale, options);
  cache.set(key, made);
  return made;
};

/**
 * One value, written the way its metric says it should be written.
 *
 * Money stays money: the currency belongs to the number, so the compact form
 * is `SAR 2.5B` and never a bare `2.5B`. Compact is for the places that
 * genuinely run out of room (an axis, a headline forty pixels tall) and every
 * one of them also offers the exact figure - on the tile itself where there is
 * room for it, on the title attribute where there is not.
 *
 * `language` defaults to the interface language set through the i18n module;
 * a test or an export in a fixed language passes it explicitly.
 */
export function formatMetric(
  value: number | null | undefined,
  format: MetricFormat | string = 'number_0',
  options: { compact?: boolean; unit?: string | null; language?: MetricLanguage } = {},
): string {
  if (value == null || !Number.isFinite(value)) return '-';
  const language = options.language ?? metricLanguage;
  const locale = METRIC_LOCALES[language];

  if (format.startsWith('currency')) {
    const digits = decimalsOf(format, 0);
    return numberFormat(
      {
        style: 'currency',
        currency: CURRENCY,
        notation: options.compact ? 'compact' : 'standard',
        minimumFractionDigits: options.compact ? undefined : digits,
        maximumFractionDigits: options.compact ? 1 : digits,
      },
      locale,
    ).format(value);
  }

  if (format.startsWith('percent')) {
    const digits = decimalsOf(format, 1);
    return `${numberFormat({ minimumFractionDigits: digits, maximumFractionDigits: digits }, locale).format(
      value * 100,
    )}%`;
  }

  const digits = decimalsOf(format, 0);
  const compact = options.compact && Math.abs(value) >= 10_000;
  const body = numberFormat(
    {
      notation: compact ? 'compact' : 'standard',
      minimumFractionDigits: compact ? undefined : digits,
      maximumFractionDigits: compact ? 1 : digits,
    },
    locale,
  ).format(value);
  // The registry states the unit separately from the format: a cycle time is
  // `number_1` with unit `days`, and "18.4" on its own is not an answer to
  // "how long does a requisition take". SAR is excluded because a currency is
  // carried by the currency formats above, never appended as a suffix.
  const unit = format.startsWith('days') ? 'days' : options.unit;
  return unit && unit !== CURRENCY ? `${body} ${unitWord(unit, language)}` : body;
}

/**
 * The change between two readings of the same metric.
 *
 * A percentage-point metric moves in points, not in percent of a percent:
 * a margin going from 11% to 12% has improved by 1 point, and calling that
 * "+9.1%" is how a dashboard misleads a board.
 */
export function formatMetricDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  format: MetricFormat | string = 'number_0',
  options: { language?: MetricLanguage } = {},
): { text: string; direction: 'up' | 'down' | 'flat'; ratio: number | null } | null {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  const language = options.language ?? metricLanguage;
  const locale = METRIC_LOCALES[language];
  const change = current - previous;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

  if (format.startsWith('percent')) {
    const points = change * 100;
    const digits = decimalsOf(format, 1);
    const body = numberFormat({ minimumFractionDigits: digits, maximumFractionDigits: digits }, locale).format(
      Math.abs(points),
    );
    return { text: `${change >= 0 ? '+' : '-'}${body} ${unitWord('pts', language)}`, direction, ratio: null };
  }

  if (previous === 0) {
    return { text: formatMetric(change, format, { compact: true, language }), direction, ratio: null };
  }
  const ratio = change / Math.abs(previous);
  return {
    text: `${change >= 0 ? '+' : '-'}${numberFormat({ maximumFractionDigits: 1 }, locale).format(Math.abs(ratio) * 100)}%`,
    direction,
    ratio,
  };
}
